import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from "@azure/msal-browser";
import {
  apiConfig,
  isNotificationEnabled,
} from "environments/environment.helper";
import { NgxSonnerToaster, toast } from "ngx-sonner";
import { filter, map, Observable, of, Subject, takeUntil, tap } from "rxjs";
import { AuthService } from "./core/auth/interceptors/auth.service";
import { AuthUtils } from "./core/auth/interceptors/auth.utils";
import { GeneralService } from "./services/general.service";
import { NotificationService } from "./services/notification.service";
import { UserService } from "./services/user.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [RouterOutlet, NgxSonnerToaster],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, OnDestroy {
  public theme = this.generalService.theme;
  private loginDisplay = false;

  public destroy$: Subject<any> = new Subject();

  protected readonly toast = toast;

  constructor(
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly msalService: MsalService,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly _notificationService: NotificationService,
    private readonly router: Router,
    private readonly generalService: GeneralService,
    private readonly swUpdate: SwUpdate,
  ) {
    effect(() => {
      const user = this.userService.user();

      if (user && isNotificationEnabled) {
        this._notificationService.startHubConnectionNotification().then(() => {
          this._notificationService
            .invokeNotificationsData()
            .then(() => this._notificationService.listenNotificatonChanges());
        });
      }
    });
  }

  ngOnInit(): void {
    this.msalService
      .handleRedirectObservable()
      .pipe(
        tap((res) => {
          if (res) {
            this.handleRedirectResult(res);
          }
        }),
      )
      .subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/login";
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.forceSWupdate();

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === "VERSION_READY",
          ),
        )
        .subscribe(() => {
          this.toast.info("New version available", {
            action: {
              label: "Update",
              onClick: () => {
                window.location.reload();
              },
            },
          });
        });
    }
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
    this.userService.isLoginDisplay.set(this.loginDisplay);

    const token = localStorage.getItem("aemAccessToken");

    if (!this.loginDisplay) {
      return;
    }

    if (token && !AuthUtils.isTokenExpired(token)) {
      return;
    }

    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      console.error("No active account found");
      return;
    }

    this.exchangeAccountForAemToken(activeAccount);
  }

  /**
   * Exchange the account's Microsoft ID token for an AEM token.
   *
   * The ID token cached on an MSAL account outlives its one-hour lifetime, and
   * the API rejects expired ones, so refresh it before sending it.
   */
  private exchangeAccountForAemToken(account: AccountInfo): void {
    this.userService.isLoading.set(true);

    this.resolveIdToken(account).subscribe({
      next: (idToken) => this.getToken(idToken),
      error: () => this.recoverToLogin(),
    });
  }

  private resolveIdToken(account: AccountInfo): Observable<string> {
    const cached = account.idToken;

    if (cached && !AuthUtils.isTokenExpired(cached)) {
      return of(cached);
    }

    return this.msalService
      .acquireTokenSilent({
        account,
        scopes: [...apiConfig.scopes],
        forceRefresh: true,
      })
      .pipe(
        takeUntil(this.destroy$),
        map((result) => {
          if (!result?.idToken) {
            throw new Error("Silent token acquisition returned no ID token");
          }

          return result.idToken;
        }),
      );
  }

  /**
   * Drop every stale credential — the AEM token and the MSAL cache — so the next
   * sign-in starts interactively instead of replaying a token the API refuses.
   */
  private recoverToLogin(): void {
    this.authService.clearSession();

    this.msalService.instance
      .clearCache()
      .catch(() => {})
      .finally(() => {
        this.router.navigate(["/login"], { replaceUrl: true }).finally(() => {
          this.userService.isLoading.set(false);
        });
      });
  }

  private handleRedirectResult(result: AuthenticationResult): void {
    if (result.account) {
      this.msalService.instance.setActiveAccount(result.account);
    }

    if (result.idToken) {
      this.getToken(result.idToken);
    } else {
      this.setLoginDisplay();
    }
  }

  getToken(idToken: string) {
    const existing = localStorage.getItem("aemAccessToken");
    if (existing && !AuthUtils.isTokenExpired(existing)) {
      // Token already valid (e.g. page refresh with active MSAL session)
      this.userService.isLoading.set(false);
      return;
    }

    // Signal guards to keep waiting while we exchange the MSAL token for an AEM token
    this.userService.isLoading.set(true);
    this.authService
      .signIn(idToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.router.navigate(["/home"], { replaceUrl: true }).finally(() => {
            this.userService.isLoading.set(false);
          });
        },
        error: () => this.recoverToLogin(),
      });
  }

  forceSWupdate() {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().then(() => {});
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this._notificationService.stopHubConnection();
  }
}
